import { Link } from "react-router-dom";
import type { Media } from "@/types/media";
import { getCategoryName } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";

interface MediaCardProps {
  media: Media;
}

export default function MediaCard({ media }: MediaCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(media.id);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg bg-background-50 ring-1 ring-background-200 transition duration-300 hover:-translate-y-1 hover:ring-primary-300">
      <Link to={`/detail/${media.id}`} className="relative block aspect-[2/3] w-full overflow-hidden bg-secondary-100">
        <img
          src={media.cover}
          alt={`${media.title} 影视封面`}
          title={`${media.title} - ${getCategoryName(media.type)}`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-900/85 px-2 py-1 text-xs font-semibold text-background-50">
          {getCategoryName(media.type)}
        </span>
        <span className="absolute bottom-2 right-2 rounded-md bg-accent-500 px-2 py-1 font-heading text-xs font-bold text-primary-950">
          {media.rating.toFixed(1)}
        </span>
        <div className="absolute inset-0 flex items-end bg-gradient-to-t from-primary-950/90 via-primary-950/20 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <p className="line-clamp-3 text-xs leading-relaxed text-background-100">{media.summary}</p>
        </div>
      </Link>

      <div className="flex flex-1 flex-col gap-1 p-3">
        <Link to={`/detail/${media.id}`} className="line-clamp-1 font-heading text-sm font-bold text-foreground-900 transition-colors hover:text-primary-700">
          {media.title}
        </Link>
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span>{media.year}</span>
          <span className="text-background-300">·</span>
          <span>{media.region}</span>
          <span className="text-background-300">·</span>
          <span>{media.genres.slice(0, 2).join(" / ")}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => toggleFavorite(media.id)}
        aria-label={favorited ? "取消收藏" : "收藏"}
        className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-base transition-colors ${
          favorited
            ? "bg-accent-500 text-primary-950"
            : "bg-primary-950/60 text-background-50 hover:bg-accent-500 hover:text-primary-950"
        }`}
      >
        <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
      </button>
    </div>
  );
}